import React  from 'react';
import { Placeholder, RichText, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import Helmet from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/app.scss';
import clsx from 'clsx';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }
  
  componentDidCatch(error, errorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo
    })
    // You can also log error messages to an error reporting service here
  }
  
  render() {
    if (this.state.errorInfo) {
      // Error path
      return (
        <div className='content add-margins'>
          <h2>Something went wrong in {this.props.component.props.rendering.componentName}</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo.componentStack}
          </details>
        </div>
      );
    }
    // Normally, just render children
    return this.props.children;
  }  
}

const Layout = ({ route, context }) => {
  const metaTags = [
    { key: 'author', value: 'MetaAuthor' },
    { key: 'description', value: 'MetaDescription' },
    { key: 'keywords', value: 'MetaKeywords' },
    { key: 'og:description', value: 'OpenGraphDescription' },
    { key: 'og:image', value: 'OpenGraphImageUrl' },
    { key: 'og:title', value: 'OpenGraphTitle' },
    { key: 'og:type', value: 'OpenGraphType' },
    { key: 'og:site', value: 'OpenGraphSiteName' },
    { key: 'og:url', value: 'OpenGraphUrl' },
    { key: 'twitter:card', value: 'TwitterCardType' },
    { key: 'twitter:creator', value: 'TwitterSite' },
    { key: 'twitter:description', value: 'TwitterDescription' },
    { key: 'twitter:image', value: 'TwitterImage' },
    { key: 'twitter:title', value: 'TwitterTitle' },
  ];

  return (
    <>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <html lang={route.itemLanguage} />
        <link rel="shortcut icon" href={context?.siteConfigurations?.favIcon} />
        {!!context?.siteConfigurations?.headTopScript &&
          <script type="text/javascript">{context?.siteConfigurations?.headTopScript.replace('<script>', '').replace('</script>', '')}</script>
        }
        <meta name="facebook-domain-verification" content="tqohduupxgglvss60vwe84gd9qm3l9" />
        <title>{route.fields?.pageTitle?.value || 'Page'}</title>
        <link
          rel="canonical"
          href={!!route?.fields?.MetaCanonical?.value ? route.fields.MetaCanonical.value : ''}
        />
        {metaTags.map((meta) => {
          let value = !!route?.fields[meta.value]?.value ? route?.fields[meta.value]?.value : '';
          if (meta.key.includes('image')) {
            value = !!route?.fields[meta.value]?.value?.src
              ? route?.fields[meta.value]?.value?.src
              : '';
          }
          return <meta key={meta.key} name={meta.key} content={value} />;
        })}
        <meta name='og:locale' content={route?.fields?.OpenGraphLocale?.value || context?.language} />
      </Helmet>
      {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}
      <VisitorIdentification />
      {/* root placeholder for the app, which we add components to using route data */}
      <div className={clsx(`page-${route?.displayName}`, 'components-wrapper', !!route?.fields?.pageTheme?.fields?.DropdownValue?.value && `${route?.fields?.pageTheme?.fields?.DropdownValue?.value}-theme`)}>
        <Placeholder name="jss-header" rendering={route} render={ComponentTemplate}  />
        <Placeholder name="jss-main" routeData={route?.fields} rendering={route}  render={ComponentTemplate} />
        <Placeholder name="jss-footer" rendering={route} render={ComponentTemplate} />
        {!!context?.siteConfigurations?.bodyTopScript &&
          <RichText field={{ value: context?.siteConfigurations?.bodyTopScript }} />
        }
      </div>
    </>
  );
};

export default Layout;

function ComponentTemplate(components) {
  return (
    <>
      {components.map((component, index) => {
        try {
          return (
            <ErrorBoundary component={component} key={index}>
              {component}
            </ErrorBoundary>
          );
        } catch (error) {
          return (
            <span>There was an error {error.message}</span>
          )
        }
      })}
    </>
  );
}